import styled from '@emotion/styled'
import { AppStore } from 'core/shared'
import { format } from 'date-fns'
import { useState } from 'react'
import FullScreen from 'screenfull'
import { api, UserManuals } from 'stylewhere/api'
import { Box, Icons, KeyValueRow, LeftContainer, SettingsModal, Spacer, VersionBadge } from 'stylewhere/components'
import { config } from 'stylewhere/config'
import { __, T } from 'stylewhere/i18n'
import {
  askUserConfirmation,
  closeModal,
  openModal,
  TEMERA_SUPPORT_EMAIL,
  TEMERA_SUPPORT_PHONE
} from 'stylewhere/utils'

export function LeftHeader({
  header = true,
  style,
  refreshOperation,
  children,
  onBugReportClick
}: React.PropsWithChildren<{ header?: boolean; style?: any; refreshOperation?: any; onBugReportClick?: () => void }>) {
  const [settingsModalVisible, setsettingsModalVisible] = useState(false)

  const isDevelopment = process.env.NODE_ENV === 'development'
  const sentryBugReportEnabled =
    AppStore.getApplicationSettingValue('enableSentryMonitoring', 'False').toLowerCase() === 'true' &&
    AppStore.getApplicationSettingValue('enableSentryFeedbackUser', 'False').toLowerCase() === 'true'
  const datadogBugReportEnabled =
    AppStore.getApplicationSettingValue('dataDogEnabled', 'False').toLowerCase() === 'true' &&
    AppStore.getApplicationSettingValue('enableDataDogFeedbackUser', 'False').toLowerCase() === 'true'
  const stwBugReportEnabled =
    AppStore.getApplicationSettingValue('enableStwFeedbackUser', 'False').toLowerCase() === 'true' &&
    AppStore.hasAnyCapabilities('System.Ticketing.open')

  const bugReportEnabled =
    (sentryBugReportEnabled || datadogBugReportEnabled || stwBugReportEnabled || isDevelopment) && !!onBugReportClick

  const removeCustomEndpoint = async () => {
    const ok = await askUserConfirmation(
      'Rimuovi Endpoint Custom',
      `Vuoi tornare all'endpoint utilizzato di default nella station?`
    )

    if (ok) AppStore.setEndpoint(undefined)
  }

  const showSupportInfoModal = () => {
    const supportModalId = `support-${Date.now()}`

    openModal({
      id: supportModalId,
      title: __(T.titles.support),
      size: 'xl',
      closeOnOverlayClick: true,
      body: (
        <Box>
          <a style={{ outline: 'none', padding: 0 }} href={`mailto:${TEMERA_SUPPORT_EMAIL}`}>
            <KeyValueRow
              style={{ width: '100%' }}
              label="Mail"
              variant="grey"
              valueStyle={{ textAlign: 'left' }}
              value={TEMERA_SUPPORT_EMAIL}
            />
          </a>
          <Spacer />
          <a style={{ outline: 'none', padding: 0 }} href={`tel:${TEMERA_SUPPORT_PHONE}`}>
            <KeyValueRow
              style={{ width: '100%' }}
              label={__(T.misc.phone)}
              variant="grey"
              valueStyle={{ textAlign: 'left' }}
              value={TEMERA_SUPPORT_PHONE}
            />
          </a>
          <Spacer />
          <KeyValueRow
            label={__(T.misc.version)}
            variant="grey"
            value={`${process.env.REACT_APP_VERSION}-${process.env.REACT_APP_BUILD_COMMIT}${
              process.env.REACT_APP_BUILD_DATE &&
              format(new Date(process.env.REACT_APP_BUILD_DATE), ' (MM/dd/yyyy HH:mm)')
            }`}
          />
          {AppStore.getUserManualFilename() && (
            <>
              <Spacer />
              <KeyValueRow
                style={{ width: '100%' }}
                variant="default"
                valueStyle={{ textAlign: 'left' }}
                value={__(T.misc.user_manual)}
                onClick={() => UserManuals.openManualPdf()}
                rightIcon={<Icons.ArrowRight />}
              />
            </>
          )}
          {bugReportEnabled && (
            <>
              <Spacer />
              <KeyValueRow
                rightIcon={<Icons.Bug style={{ width: 36 }} />}
                variant="default"
                valueStyle={{ textAlign: 'left' }}
                value="Bug Report"
                onClick={() => {
                  closeModal(supportModalId)
                  onBugReportClick?.()
                }}
              />
            </>
          )}
        </Box>
      )
    })
  }

  const getControlPanelURL = () => {
    let url
    if (globalThis.location?.origin && globalThis.location.hostname !== 'localhost') {
      url = globalThis.location.origin
      url = url.replace('station', 'controlpanel')
    }
    return url
  }

  const openControlPanel = () => {
    window.open(getControlPanelURL(), '_blank')
  }

  return (
    <Container style={style}>
      {header && (
        <Box center mt={120}>
          <LogoContainer>
            <Icons.RfidStation width={380} height={160} />
            <VersionBadge />
          </LogoContainer>
        </Box>
      )}
      <LeftContainer style={{ alignItems: 'center' }}>
        {children}
        <Spacer />
        {api.getBaseURL() !== config.endpoint && (
          <KeyValueRow
            variant="inset"
            label="Custom backend"
            valueStyle={{ fontSize: 18 }}
            value={api.getBaseURL()}
            onClick={removeCustomEndpoint}
          />
        )}
      </LeftContainer>
      <BottomButtons row>
        <BottomButton data-testid="bottom-buttons" data-cy="Settings" onClick={() => setsettingsModalVisible(true)}>
          <Icons.Settings style={{ width: 36, margin: 17, opacity: 0.8 }} />
        </BottomButton>
        <Spacer />
        <BottomButton
          data-testid="bottom-buttons"
          data-cy="Fullscreen"
          onClick={() => {
            // @ts-ignore
            FullScreen.toggle()
          }}
        >
          <Icons.Fullscreen style={{ width: 36, margin: 17, opacity: 0.8 }} />
        </BottomButton>
        {getControlPanelURL() && (
          <>
            <Spacer />
            <BottomButtonCP data-testid="bottom-buttons" data-cy="ControlPanel" onClick={openControlPanel}>
              <Icons.ControlPanel style={{ width: 66, margin: 0, opacity: 0.8 }} />
            </BottomButtonCP>
          </>
        )}
        <Spacer />
        <BottomButton data-testid="bottom-buttons" data-cy="Support" onClick={showSupportInfoModal}>
          <Icons.Help style={{ width: 36, margin: 17, opacity: 0.8 }} />
        </BottomButton>
        <Spacer />
        <BottomButton data-testid="bottom-buttons" data-cy="Refresh" onClick={refreshOperation}>
          <Icons.Refresh style={{ width: 36, height: 36, margin: 17, opacity: 0.8 }} />
        </BottomButton>
      </BottomButtons>
      {settingsModalVisible && (
        <SettingsModal onClose={() => setsettingsModalVisible(false)} visible={settingsModalVisible} />
      )}
    </Container>
  )
}

const Container = styled(Box)`
  background-color: ${({ theme }) => theme.background1};
  overflow-y: hidden;
  width: 550px;
  max-width: 550px;
  min-width: 550px;
`

const LogoContainer = styled.div`
  position: relative;
  width: 380px;
  height: 160px;
`

const BottomButtons = styled(Box)`
  position: absolute;
  bottom: 50px;
  left: 50px;
`

const bottomButtonShadow = `
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.2);
  }
`

const BottomButton = styled.div`
  background: #ffffff;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: center;
  cursor: pointer;
  ${bottomButtonShadow}
`

const BottomButtonCP = styled.div`
  background: #ffffff;
  border-radius: 10px;
  width: 70px;
  height: 70px;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: center;
  cursor: pointer;
  ${bottomButtonShadow}
`
